import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import AnimatedLoadingState from '../components/AnimatedLoadingState';
import ErrorState from '../components/ErrorState';

// ============================================================
// Типы
// ============================================================

type ElectionalType =
  | 'wedding'
  | 'business'
  | 'contract'
  | 'travel'
  | 'relocation'
  | 'surgery'
  | 'investment'
  | 'launch'
  | 'home-purchase'
  | 'general';

interface ElectionalLocation {
  latitude: number;
  longitude: number;
  timezone?: string;
}

interface ElectionalDateResult {
  date: string;
  score: number;
  moonPhase: string;
  moonSign: string;
  recommendations: string[];
  aspects: string[];
  notes: string;
}

interface ElectionalInterpretation {
  interpretation: string;
  bestDate: string;
  reasoning: string;
  warnings: string[];
}

// ============================================================
// Данные типов событий
// ============================================================

const EVENT_TYPES: { value: ElectionalType; label: string; icon: string }[] = [
  { value: 'wedding', label: 'Свадьба', icon: '💍' },
  { value: 'business', label: 'Бизнес', icon: '🏢' },
  { value: 'contract', label: 'Контракт', icon: '📝' },
  { value: 'travel', label: 'Путешествие', icon: '✈️' },
  { value: 'relocation', label: 'Переезд', icon: '🏠' },
  { value: 'surgery', label: 'Операция', icon: '🏥' },
  { value: 'investment', label: 'Инвестиции', icon: '📈' },
  { value: 'launch', label: 'Начало работы', icon: '🚀' },
  { value: 'home-purchase', label: 'Покупка дома', icon: '🏡' },
  { value: 'general', label: 'Общее', icon: '🌟' },
];

// ============================================================
// Стили
// ============================================================

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)',
  padding: '20px 20px',
};

const containerStyle: React.CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(32px, 5vw, 48px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '12px',
  textAlign: 'center' as const,
};

const subtitleStyle: React.CSSProperties = {
  color: '#b0b0b0',
  fontSize: '16px',
  textAlign: 'center' as const,
  marginBottom: '32px',
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  padding: '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
};

const btnPrimaryStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '12px 28px',
  borderRadius: '25px',
  color: '#0a0a1a',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const btnSecondaryStyle: React.CSSProperties = {
  background: 'rgba(212, 175, 55, 0.1)',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  color: '#d4af37',
  padding: '10px 24px',
  borderRadius: '25px',
  fontSize: '14px',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '15px',
  boxSizing: 'border-box' as const,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#d4af37',
  marginBottom: '6px',
  fontSize: '13px',
};

// ============================================================
// Компонент
// ============================================================

const ElectionalPage: React.FC = () => {
  const { user } = useAuth();

  const [eventType, setEventType] = useState<ElectionalType>('wedding');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useProfileLocation, setUseProfileLocation] = useState(true);
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [timezone, setTimezone] = useState('');

  const [results, setResults] = useState<ElectionalDateResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interpretation, setInterpretation] = useState<ElectionalInterpretation | null>(null);
  const [interpretationLoading, setInterpretationLoading] = useState(false);

  // Инициализация координат из профиля
  const initializeLocation = useCallback(() => {
    if (user?.birth_place) {
      setUseProfileLocation(true);
    }
  }, [user]);

  React.useEffect(() => {
    initializeLocation();
  }, [initializeLocation]);

  // Поиск дат
  const findDates = async () => {
    if (!startDate || !endDate) {
      setError('Укажите диапазон дат');
      return;
    }

    const location: ElectionalLocation = {
      latitude: parseFloat(lat) || 55.7558,
      longitude: parseFloat(lon) || 37.6173,
      timezone: timezone || undefined,
    };

    setLoading(true);
    setError(null);
    setResults([]);
    setInterpretation(null);

    const res = await api.post<ElectionalDateResult[]>('/electional/find', {
      type: eventType,
      startDate,
      endDate,
      location,
    });

    if (res.success && res.data) {
      const sorted = [...res.data].sort((a, b) => b.score - a.score);
      setResults(sorted);
    } else {
      setError(res.error || 'Не удалось найти даты');
    }
    setLoading(false);
  };

  // AI-интерпретация
  const getInterpretation = async () => {
    if (results.length === 0) return;

    setInterpretationLoading(true);
    const res = await api.post<ElectionalInterpretation>('/electional/interpret', {
      type: eventType,
      dates: results.slice(0, 5).map((r) => ({
        date: r.date,
        score: r.score,
      })),
    });

    if (res.success && res.data) {
      setInterpretation(res.data);
    }
    setInterpretationLoading(false);
  };

  // Score color
  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#7dd3c0';
    if (score >= 40) return '#ffc107';
    return '#dc3545';
  };

  // Score label
  const getScoreLabel = (score: number): string => {
    if (score >= 80) return 'Отлично';
    if (score >= 60) return 'Хорошо';
    if (score >= 40) return 'Средне';
    return 'Неблагоприятно';
  };

  return (
    <div style={pageStyle}>
      <div style={{ ...containerStyle, position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
        {/* Заголовок */}
        <h1 style={titleStyle}>Выбор даты</h1>
        <p style={subtitleStyle}>
          Элективная астрология — поиск лучшего времени для важных событий
        </p>

        {error && <ErrorState message={error} onRetry={findDates} />}

        {/* Форма */}
        <div style={{ ...cardStyle, marginBottom: '32px' }}>
          <h2
            style={{
              color: '#d4af37',
              fontSize: '20px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              margin: '0 0 24px',
            }}
          >
            Параметры поиска
          </h2>

          <div style={{ display: 'grid', gap: '20px' }}>
            {/* Тип события */}
            <div>
              <label style={labelStyle}>Тип события</label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '8px',
                }}
              >
                {EVENT_TYPES.map((et) => (
                  <button
                    key={et.value}
                    onClick={() => setEventType(et.value)}
                    style={{
                      background:
                        eventType === et.value
                          ? 'rgba(212, 175, 55, 0.2)'
                          : 'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${eventType === et.value ? '#d4af37' : 'rgba(212, 175, 55, 0.15)'}`,
                      borderRadius: '10px',
                      padding: '10px 12px',
                      color: eventType === et.value ? '#d4af37' : '#888',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                      textAlign: 'center' as const,
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{ fontSize: '18px', display: 'block', marginBottom: '4px' }}>
                      {et.icon}
                    </span>
                    {et.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Диапазон дат */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Начальная дата</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Конечная дата</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Место */}
            <div>
              <label style={labelStyle}>Место события</label>
              <div style={{ marginBottom: '12px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#b0b0b0',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={useProfileLocation}
                    onChange={(e) => setUseProfileLocation(e.target.checked)}
                    style={{ accentColor: '#d4af37' }}
                  />
                  Использовать место рождения из профиля
                </label>
              </div>

              {!useProfileLocation && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Широта</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={lat}
                      onChange={(e) => setLat(e.target.value)}
                      placeholder="55.7558"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Долгота</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={lon}
                      onChange={(e) => setLon(e.target.value)}
                      placeholder="37.6173"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Часовой пояс</label>
                    <input
                      type="text"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      placeholder="Europe/Moscow"
                      style={inputStyle}
                    />
                  </div>
                </div>
              )}

              {useProfileLocation && user?.birth_place && (
                <p style={{ color: '#888', fontSize: '14px', margin: '8px 0 0' }}>
                  Место: {user.birth_place}
                </p>
              )}
            </div>

            {/* Кнопка */}
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <button
                style={{
                  ...btnPrimaryStyle,
                  opacity: loading ? 0.7 : 1,
                }}
                onClick={findDates}
                disabled={loading}
              >
                {loading ? 'Поиск...' : 'Найти даты'}
              </button>
            </div>
          </div>
        </div>

        {/* Результаты */}
        {loading && <AnimatedLoadingState />}

        {results.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <h2
                style={{
                  color: '#d4af37',
                  fontSize: '22px',
                  fontFamily: 'var(--font-display)',
                  fontStyle: 'italic',
                  margin: 0,
                }}
              >
                Найденные даты ({results.length})
              </h2>
              {!interpretationLoading && !interpretation && (
                <button style={btnSecondaryStyle} onClick={getInterpretation}>
                  ✨ Интерпретация
                </button>
              )}
            </div>

            {/* Список дат */}
            <div style={{ display: 'grid', gap: '12px', marginBottom: '32px' }}>
              {results.map((result, i) => (
                <div
                  key={i}
                  style={{
                    ...cardStyle,
                    borderLeft: `4px solid ${getScoreColor(result.score)}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '16px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3
                        style={{
                          color: '#f8f8f8',
                          fontSize: '18px',
                          margin: 0,
                          fontFamily: 'var(--font-sans)',
                        }}
                      >
                        {new Date(result.date).toLocaleDateString('ru-RU', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </h3>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <span style={{ color: '#888', fontSize: '13px' }}>
                        🌙 {result.moonPhase}
                      </span>
                      {result.moonSign && (
                        <span style={{ color: '#888', fontSize: '13px' }}>
                          Знак Луны: {result.moonSign}
                        </span>
                      )}
                    </div>

                    {result.aspects.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#666', fontSize: '12px' }}>Аспекты: </span>
                        <span style={{ color: '#b0b0b0', fontSize: '12px' }}>
                          {result.aspects.join(', ')}
                        </span>
                      </div>
                    )}

                    {result.recommendations.length > 0 && (
                      <ul
                        style={{
                          color: '#e0e0e0',
                          fontSize: '13px',
                          lineHeight: 1.6,
                          paddingLeft: '20px',
                          margin: '8px 0 0',
                        }}
                      >
                        {result.recommendations.slice(0, 3).map((rec, j) => (
                          <li key={j}>{rec}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div
                    style={{
                      textAlign: 'center',
                      minWidth: '80px',
                      background: `${getScoreColor(result.score)}15`,
                      border: `1px solid ${getScoreColor(result.score)}33`,
                      borderRadius: '12px',
                      padding: '12px 16px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        color: getScoreColor(result.score),
                        fontFamily: 'var(--font-display)',
                      }}
                    >
                      {result.score}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: getScoreColor(result.score),
                        marginTop: '2px',
                      }}
                    >
                      {getScoreLabel(result.score)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* AI-интерпретация */}
            {interpretationLoading && <AnimatedLoadingState />}
            {interpretation && (
              <div style={cardStyle}>
                <h3
                  style={{
                    color: '#d4af37',
                    fontSize: '18px',
                    fontFamily: 'var(--font-display)',
                    fontStyle: 'italic',
                    marginBottom: '12px',
                    marginTop: 0,
                  }}
                >
                  Интерпретация
                </h3>

                {interpretation.bestDate && (
                  <div
                    style={{
                      background: 'rgba(40, 167, 69, 0.1)',
                      border: '1px solid rgba(40, 167, 69, 0.3)',
                      borderRadius: '10px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                    }}
                  >
                    <span style={{ color: '#28a745', fontSize: '14px', fontWeight: 600 }}>
                      Лучшая дата:{' '}
                    </span>
                    <span style={{ color: '#f8f8f8', fontSize: '14px' }}>
                      {new Date(interpretation.bestDate).toLocaleDateString('ru-RU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}

                <p
                  style={{
                    color: '#e0e0e0',
                    fontSize: '15px',
                    lineHeight: 1.8,
                    marginBottom: '16px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {interpretation.interpretation}
                </p>

                {interpretation.reasoning && (
                  <div
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(212, 175, 55, 0.15)',
                      borderRadius: '10px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <h4 style={{ color: '#d4af37', fontSize: '14px', marginBottom: '8px', marginTop: 0 }}>
                      Обоснование
                    </h4>
                    <p
                      style={{
                        color: '#b0b0b0',
                        fontSize: '14px',
                        lineHeight: 1.7,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {interpretation.reasoning}
                    </p>
                  </div>
                )}

                {interpretation.warnings.length > 0 && (
                  <div>
                    <h4 style={{ color: '#dc3545', fontSize: '14px', marginBottom: '8px' }}>
                      Предупреждения
                    </h4>
                    <ul
                      style={{
                        color: '#e0e0e0',
                        fontSize: '14px',
                        lineHeight: 1.8,
                        paddingLeft: '20px',
                        margin: 0,
                      }}
                    >
                      {interpretation.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ElectionalPage;
